import {
  Avatar,
  Button,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { formatDate } from '@utils/format';

import {
  useDeleteEmrConnection,
  useGetAuthorizationUrlLazyQuery,
} from 'generated/emr.generated';
import type {
  DirectEmrApiConnection,
  DirectEmrApiEndpoint as Integration,
} from 'generated/types';

const messages = defineMessages({
  connect: {
    id: 'connectionsMain.emrConnect',
    defaultMessage: 'Connect',
  },
  disconnect: {
    id: 'connectionsMain.emrDisconnect',
    defaultMessage: 'Disconnect',
  },
  connected: {
    id: 'connectionsMain.emrConnected',
    defaultMessage: 'Connected',
  },
  error: {
    id: 'connectionsMain.emrError',
    defaultMessage:
      'An error occurred while trying to connect to a hospital or clinic integration',
  },
  dateConnected: {
    id: 'connectionsMain.emrDateConnected',
    defaultMessage: 'Date Connected: {date}',
  },
  lastUpdate: {
    id: 'connectionsMain.emrLastUpdate',
    defaultMessage: 'Last Update: {date}',
  },
});

interface EMRIntegrationProps {
  /**
   * Name and image of Terra integration
   */
  integration: Integration;
  /**
   * User's connected Terra device
   */
  connection?: DirectEmrApiConnection;
}

export default function EMRIntegration({
  integration,
  connection,
}: EMRIntegrationProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile },
  } = useSession();

  const ref = useRef<HTMLButtonElement | null>(null);

  const [deleteEmrConnection, { loading: deleteEmrConnectionLoading }] =
    useDeleteEmrConnection();

  const {
    city,
    country,
    fhir_api_endpoint_id,
    organization_name,
    organization_logo_url,
    region,
  } = integration;

  const [getAuthorizationUrl, { loading: getAuthorizationUrlLoading }] =
    useGetAuthorizationUrlLazyQuery();

  const { palette } = useTheme();

  const isConnected = Boolean(connection?.expiry);

  const loading = getAuthorizationUrlLoading || deleteEmrConnectionLoading;

  const connect = async () => {
    if (!activeProfile?.vitall_patient_id || isConnected) {
      return;
    }
    const { data } = await getAuthorizationUrl({
      variables: {
        input: {
          fhir_api_endpoint_id,
          vitall_patient_id: activeProfile.vitall_patient_id,
        },
      },
      onCompleted: () => {
        enqueueSnackbar({
          variant: 'error',
          message: formatMessage(messages.error),
        });
      },
    });

    const authorizationUrl = data?.getDirectEmrApiAuthorizationURL;
    if (authorizationUrl) {
      // Open integration's authentication route
      // Upon authentication completion, the user will be navigated to /emr-integrator/[result]
      open(authorizationUrl, '_blank');
    }
  };

  const disconnect = async () => {
    if (!activeProfile?.vitall_patient_id || !isConnected || !connection) {
      return;
    }
    const { fhir_api_endpoint_id } = connection;
    await deleteEmrConnection({
      variables: {
        input: {
          vitall_patient_id: activeProfile.vitall_patient_id,
          fhir_api_endpoint_id,
        },
      },
    });
  };

  const handleClick = async () => {
    if (isConnected) {
      await disconnect();
    } else {
      await connect();
    }
  };

  return (
    <ListItem>
      <ListItemAvatar sx={{ marginRight: '10px' }}>
        <Avatar
          alt={organization_name}
          sx={{ width: 50, height: 50 }}
          src={organization_logo_url ?? ''}
        />
      </ListItemAvatar>
      <ListItemText
        primary={<Typography variant="h6">{organization_name}</Typography>}
        secondary={
          isConnected ? (
            <>
              <Typography variant="subtitle2">
                {formatMessage(messages.dateConnected, {
                  date: connection?.created_at
                    ? formatDate({
                        date: connection.created_at,
                      })
                    : '-',
                })}
              </Typography>
              <Typography variant="subtitle2">
                {formatMessage(messages.lastUpdate, {
                  date: connection?.updated_at
                    ? formatDate({ date: connection.updated_at })
                    : '-',
                })}
              </Typography>
            </>
          ) : (
            <>
              {(city || country || region) && (
                <Typography variant="subtitle2">
                  {[city, region, country]
                    .filter((location) => location)
                    .join(', ')}
                </Typography>
              )}
            </>
          )
        }
      />
      <Button
        disabled={loading}
        onClick={handleClick}
        ref={ref}
        onMouseOut={() => {
          if (ref.current?.innerText && isConnected) {
            ref.current.innerText = formatMessage(messages.connected);
          }
        }}
        onMouseOver={() => {
          if (ref.current?.innerText && isConnected) {
            ref.current.innerText = formatMessage(messages.disconnect);
          }
        }}
        sx={{
          backgroundColor: isConnected
            ? palette.success.main
            : palette.primary.main,
          '&:hover': {
            backgroundColor: isConnected
              ? palette.error.main
              : palette.primary.main,
          },
        }}
      >
        {formatMessage(isConnected ? messages.connected : messages.connect)}
      </Button>
    </ListItem>
  );
}
